"use client";

import { normalPdf } from "@/lib/stats/distributions";
import { CHART_COLORS } from "@/lib/tool-kit/palette";
import {
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import { niceTicks } from "@/lib/tool-kit/stats-descriptive/ticks";
import type { HistogramBin } from "@/lib/tool-kit/stats-descriptive-more/moments";

const WIDTH = 640;
const HEIGHT = 260;
const PAD_LEFT = 40;
const PAD_RIGHT = 20;
const TOP = 26;
const AXIS_Y = 214;
const CURVE_STEPS = 80;

export interface HistogramMarker {
  value: number;
  /** Short text drawn at the top of the line. */
  label: string;
  color?: string;
  dashed?: boolean;
}

/**
 * A histogram drawn to scale, with an optional normal curve scaled to the same area and
 * optional vertical markers (a mean, a confidence interval). The bars and the curve use
 * different colours and the curve is also a line, so colour is not the only cue; the
 * accessible description comes from the caller.
 */
export function HistogramPlot({
  bins,
  fmt,
  label,
  normal,
  markers = [],
  yLabel,
}: {
  bins: readonly HistogramBin[];
  fmt: NumberFormatter;
  label: string;
  /** Overlay a normal density with this mean and SD, scaled to the data's n. */
  normal?: { mean: number; sd: number; n: number };
  markers?: readonly HistogramMarker[];
  yLabel: string;
}) {
  const dataLo = bins[0].from;
  const dataHi = (bins.at(-1) as HistogramBin).to;
  const width = bins[0].to - bins[0].from;
  const markerValues = markers.map((marker) => marker.value);
  const axis = niceTicks(
    Math.min(dataLo, ...markerValues),
    Math.max(dataHi, ...markerValues),
    6
  );
  const span = axis.hi - axis.lo || 1;
  const x = (value: number) =>
    PAD_LEFT + ((value - axis.lo) / span) * (WIDTH - PAD_LEFT - PAD_RIGHT);
  const curve = (value: number) =>
    normal ? normal.n * width * normalPdf(value, normal.mean, normal.sd) : 0;
  const curvePoints = normal
    ? Array.from({ length: CURVE_STEPS + 1 }, (_, i) => {
        const value = dataLo + ((dataHi - dataLo) * i) / CURVE_STEPS;
        return { value, height: curve(value) };
      })
    : [];
  const peak = Math.max(
    ...bins.map((bin) => bin.count),
    ...curvePoints.map((point) => point.height)
  );
  const yAxis = niceTicks(0, peak, 4);
  const y = (value: number) =>
    AXIS_Y - (value / (yAxis.hi || 1)) * (AXIS_Y - TOP);

  return (
    <svg
      aria-label={label}
      className="h-auto w-full text-muted-foreground"
      role="img"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
    >
      {yAxis.ticks.map((tick) => (
        <g key={tick}>
          <line
            opacity={0.2}
            stroke="currentColor"
            x1={PAD_LEFT}
            x2={WIDTH - PAD_RIGHT}
            y1={y(tick)}
            y2={y(tick)}
          />
          <text
            fill="currentColor"
            fontSize={11}
            textAnchor="end"
            x={PAD_LEFT - 6}
            y={y(tick) + 4}
          >
            {formatStat(fmt, tick)}
          </text>
        </g>
      ))}
      <text
        fill="currentColor"
        fontSize={11}
        textAnchor="middle"
        transform={`rotate(-90 12 ${(TOP + AXIS_Y) / 2})`}
        x={12}
        y={(TOP + AXIS_Y) / 2}
      >
        {yLabel}
      </text>
      {bins.map((bin) => (
        <rect
          fill={CHART_COLORS[0]}
          fillOpacity={0.55}
          height={Math.max(0, AXIS_Y - y(bin.count))}
          key={bin.from}
          stroke={CHART_COLORS[0]}
          width={Math.max(1, x(bin.to) - x(bin.from) - 1)}
          x={x(bin.from)}
          y={y(bin.count)}
        />
      ))}
      {normal ? (
        <polyline
          fill="none"
          points={curvePoints
            .map((point) => `${x(point.value)},${y(point.height)}`)
            .join(" ")}
          stroke={CHART_COLORS[3]}
          strokeWidth={2}
        />
      ) : null}
      {markers.map((marker, index) => (
        <g
          // Markers are positional and may share a value.
          // biome-ignore lint/suspicious/noArrayIndexKey: positional markers
          key={index}
        >
          <line
            stroke={marker.color ?? CHART_COLORS[1]}
            strokeDasharray={marker.dashed ? "5 4" : undefined}
            strokeWidth={2}
            x1={x(marker.value)}
            x2={x(marker.value)}
            y1={TOP - 4}
            y2={AXIS_Y}
          />
          <text
            fill="currentColor"
            fontSize={11}
            textAnchor="middle"
            x={x(marker.value)}
            y={TOP - 9}
          >
            {marker.label}
          </text>
        </g>
      ))}
      <line
        stroke="currentColor"
        x1={PAD_LEFT}
        x2={WIDTH - PAD_RIGHT}
        y1={AXIS_Y}
        y2={AXIS_Y}
      />
      {axis.ticks.map((tick) => (
        <g key={tick}>
          <line
            stroke="currentColor"
            x1={x(tick)}
            x2={x(tick)}
            y1={AXIS_Y}
            y2={AXIS_Y + 5}
          />
          <text
            fill="currentColor"
            fontSize={12}
            textAnchor="middle"
            x={x(tick)}
            y={AXIS_Y + 20}
          >
            {formatStat(fmt, tick)}
          </text>
        </g>
      ))}
    </svg>
  );
}
